import { useMemo, useState } from 'react'
import {
  deleteCancelCte,
  getCtesByTp,
  getDadosSubstituicaoCte,
  postSyncCte,
} from '../../../../services/api/https/deliveries/cte.http'
import { postEmitMdfe } from '../../../../services/api/https/deliveries/mdfe.http'
import { urlDownloadCte } from '../../../../services/api/routes/cte.routes'
import { getPeriodoMesAtual } from '../../../../services/util/date.service'
import { errorHandler } from '../../../../services/util/error.service'
import { gerarArquivoExcel } from '../../../../services/util/excel.service'

const INITIAL_STATE = {
  errorMessage: null,
  loadingMessage: null,
  ctes: [],
  filterdCtes: [],
  modal: { visible: false, cte: null },
  modalMdfe: { visible: false, ctes: null, data: null },
  modalComplementar: { visible: false, cte: null },
  modalSubstituicao: { visible: false, cte: null, dados: null },
  selectedCte: null,
  selectedStatus: [],
  selectedCidades: [],
  selectedEmpresas: [],
  selectedGlobalizado: null,
  selectedTipoReferencia: null,
  selectedTipoDocumento: null,
  selectedTipoServico: null,
  tiposReferenciaOptions: [],
  tiposDocumentoOptions: [],
  tiposServicoOptions: [],
  cidadesOptions: [],
  empresasOptions: [],
  selecionarTodos: false,
  periodo: getPeriodoMesAtual(),
}

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCH: 'Buscando CTes',
  DELETE: 'Excluindo CTe',
  SUCCESS_DELETE: 'CTe excluida com sucesso',
  SYNC: 'Sincronizando status do CTe',
  SUCCESS_SYNC: 'Status do CTe sincronizado com sucesso',
  EMIT_MDFE: 'Solicitando MDFe',
  SUCCESS_EMIT_MDFE: 'MDFe solicitado com sucesso',
  SUCCESS_EMIT_COMPLEMENTAR: 'CTe Complementar enviado para emissão, aguardando resposta SEFAZ',
  FETCH_SUBSTITUICAO: 'Buscando dados do CTe',
  SUCCESS_EMIT_SUBSTITUICAO: 'CTe de Substituição enviado para emissão, aguardando resposta SEFAZ',
}

// tpCTe da SEFAZ: 1 = CTe Complementar (complementa valores de um CTe autorizado)
export const TIPO_DOCUMENTO_COMPLEMENTAR = 1

export const isCteComplementar = cte => Number(cte.tipo_documento) === TIPO_DOCUMENTO_COMPLEMENTAR

// tpCTe da SEFAZ: 3 = CTe de Substituição (substitui um CTe autorizado, com valores ou tomador corrigidos)
export const TIPO_DOCUMENTO_SUBSTITUICAO = 3

export const isCteSubstituicao = cte => Number(cte.tipo_documento) === TIPO_DOCUMENTO_SUBSTITUICAO

// Situação do CTe pelo CTe de Substituição vigente dele: autorizado (já substituído) ou em processamento
export const isCteSubstituido = cte => cte.cte_substituto_status === 'autorizado'

export const isCteEmSubstituicao = cte => cte.cte_substituto_status === 'processando_autorizacao'

const semSubstituicaoVigente = cte => !isCteSubstituido(cte) && !isCteEmSubstituicao(cte)

// O CTe substituído continua autorizado na SEFAZ, mas deixou de valer: a tela mostra "Substituído"
export const statusExibicao = cte => (isCteSubstituido(cte) ? 'substituido' : cte.status)

export const mensagemStatus = cte => {
  if (isCteSubstituido(cte)) return `Substituído pelo CTe nº ${cte.cte_substituto_numero}`
  if (isCteEmSubstituicao(cte)) return 'Substituição em processamento na SEFAZ'
  return cte.mensagem_sefaz
}

// O CTe de Substituição não pode ser cancelado, nem o CTe com substituição autorizada ou em processamento
export const podeCancelarCte = cte =>
  cte.status === 'autorizado' && !isCteSubstituicao(cte) && semSubstituicaoVigente(cte)

// Só um CTe Normal ou de Substituição autorizado, sem substituição vigente, pode ser complementado
export const podeComplementarCte = cte =>
  cte.status === 'autorizado' && !isCteComplementar(cte) && semSubstituicaoVigente(cte)

// A opção continua disponível com a substituição em processamento: ao abrir, a API confere na Focus se a
// SEFAZ já respondeu (webhook perdido) e libera nova tentativa quando a anterior foi rejeitada
export const podeSubstituirCte = cte =>
  cte.status === 'autorizado' && !isCteComplementar(cte) && !isCteSubstituido(cte)

// CTe de Subcontratação: emitido a partir de um CT-e de origem importado (sem entregas do sistema)
export const isCteSubcontratacao = cte => cte.document_origin === 'cte'

// Valor de um dropdown com a opção "Todos" (value null); false é um valor válido (CTe globalizado)
const valorDropdown = selecionado =>
  selecionado && selecionado.value !== null && selecionado.value !== undefined
    ? selecionado.value
    : null

const opcoesUnicas = valores =>
  [...new Set(valores.filter(Boolean))].map(valor => ({ label: valor, value: valor }))

// Filtros do fieldset aplicados sobre a lista carregada; `filtros` é o state com os selected*
const applyFilters = (ctes, filtros) => {
  const statusValues = (filtros.selectedStatus || []).map(s => s.value || s)
  const cidadeValues = (filtros.selectedCidades || []).map(c => c.value || c)
  const empresaValues = (filtros.selectedEmpresas || []).map(e => e.value || e)
  const globalizadoValue = valorDropdown(filtros.selectedGlobalizado)
  const tipoReferenciaValue = valorDropdown(filtros.selectedTipoReferencia)
  const tipoDocumentoValue = valorDropdown(filtros.selectedTipoDocumento)
  const tipoServicoValue = valorDropdown(filtros.selectedTipoServico)

  return (ctes || []).filter(c => {
    if (statusValues.length && !statusValues.includes(statusExibicao(c))) return false
    if (cidadeValues.length && !cidadeValues.includes(c.client_city)) return false
    if (empresaValues.length && !empresaValues.includes(c.company_name)) return false
    if (globalizadoValue !== null && Boolean(c.globalizado) !== globalizadoValue) return false
    if (tipoReferenciaValue !== null && c.documento_referencia_tipo !== tipoReferenciaValue) return false
    if (tipoDocumentoValue !== null && c.tipo_documento_descricao !== tipoDocumentoValue) return false
    if (tipoServicoValue !== null && c.tipo_servico_descricao !== tipoServicoValue) return false

    return true
  })
}

export default function useGestaoCtes() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    let ultimaBusca = null

    const fetchCtes = async (tp_id, pInicial, pFinal) => {
      if (!tp_id) {
        setState(prevState => ({ ...prevState, errorMessage: 'Selecione um TP' }))
        return
      }

      if (!pInicial || !pFinal) {
        setState(prevState => ({
          ...prevState,
          errorMessage: 'Período inicial e final são obrigatórios',
        }))
        return
      }

      ultimaBusca = { tp_id, pInicial, pFinal }

      try {
        setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.FETCH }))
        const ctes = await getCtesByTp(tp_id, pInicial, pFinal)

        setState(prev => ({
          ...prev,
          loadingMessage: LOAD_MESSAGE.NONE,
          ctes,
          filterdCtes: applyFilters(ctes, prev),
          cidadesOptions: opcoesUnicas(ctes.map(c => c.client_city)),
          empresasOptions: opcoesUnicas(ctes.map(c => c.company_name)),
          tiposReferenciaOptions: opcoesUnicas(ctes.map(c => c.documento_referencia_tipo)),
          tiposDocumentoOptions: opcoesUnicas(ctes.map(c => c.tipo_documento_descricao)),
          tiposServicoOptions: opcoesUnicas(ctes.map(c => c.tipo_servico_descricao)),
          periodo: { dataInicial: pInicial, dataFinal: pFinal },
        }))
      } catch (error) {
        setState(prevState => ({
          ...prevState,
          errorMessage: errorHandler(error),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      }
    }

    const aplicarFiltro = (campo, valor) =>
      setState(prevState => {
        const filtros = { ...prevState, [campo]: valor }
        return { ...filtros, filterdCtes: applyFilters(prevState.ctes, filtros) }
      })

    // A API confere se o CTe pode ser substituído (prazo, complementar, substituição em andamento) e
    // devolve os prazos e os componentes do valor; o modal só abre se a substituição for possível
    const abrirSubstituicao = async cte => {
      // Com a substituição em processamento, a consulta da API à Focus pode mudar a situação dos CTes
      const listaDesatualizada = isCteEmSubstituicao(cte) && ultimaBusca
      let dados = null
      let erro = null

      try {
        setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.FETCH_SUBSTITUICAO }))
        dados = await getDadosSubstituicaoCte(cte.id)
      } catch (error) {
        erro = errorHandler(error)
      }

      if (listaDesatualizada) {
        await fetchCtes(ultimaBusca.tp_id, ultimaBusca.pInicial, ultimaBusca.pFinal)
      }

      setState(prevState => ({
        ...prevState,
        loadingMessage: LOAD_MESSAGE.NONE,
        ...(erro ? { errorMessage: erro } : { modalSubstituicao: { visible: true, cte, dados } }),
      }))
    }

    return {
      resetState() {
        setState(prev => ({ ...prev, loadingMessage: null, errorMessage: null }))
      },

      // Events
      async onInit(tp_id, pInicial, pFinal) {
        await fetchCtes(tp_id, pInicial, pFinal)
      },

      onItemClicked(cte) {
        setState(prev => {
          const { filterdCtes } = prev
          const index = filterdCtes.findIndex(c => c.id === cte.id)
          if (index !== -1) {
            filterdCtes[index].selected = !filterdCtes[index].selected
          }

          return { ...prev }
        })
      },

      onMenuItemClick(item, cte) {
        if (item === 'Cancelar CTe') {
          setState(prevState => ({ ...prevState, modal: { visible: true, cte } }))
        } else if (item === 'Sincronizar Status') {
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.SYNC,
            selectedCte: cte,
          }))
        } else if (item === 'CTe Complementar') {
          setState(prevState => ({ ...prevState, modalComplementar: { visible: true, cte } }))
        } else if (item === 'CTe de Substituição') {
          abrirSubstituicao(cte)
        } else if (item === 'DACTE') {
          window.open(cte.caminho_dacte, '_blank')
        } else if (item === 'XML') {
          window.open(cte.caminho_xml, '_blank')
        } else if (item === 'XML Cancelamento') {
          window.open(cte.caminho_xml_cancelamento, '_blank')
        }
      },

      onModalDeleteClose() {
        setState(prevState => ({
          ...prevState,
          modal: { visible: false, cte: null },
        }))
      },

      onModalError(error) {
        setState(prevState => ({
          ...prevState,
          errorMessage: errorHandler(error),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      onModalDeleteSave(cte) {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.DELETE,
          modal: { visible: false, cte: null },
          selectedCte: cte,
        }))
      },

      deleteCte(cte) {
        deleteCancelCte({ ref: cte.ref, justificativa: cte.justificativa })
          .then(() =>
            setState(prevState => {
              prevState.loadingMessage = LOAD_MESSAGE.SUCCESS_DELETE
              return { ...prevState }
            }),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },

      syncCte(cte) {
        postSyncCte({ ref: cte.ref })
          .then(() =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.SUCCESS_SYNC,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },

      onModalComplementarClose() {
        setState(prevState => ({
          ...prevState,
          modalComplementar: { visible: false, cte: null },
        }))
      },

      // O modal já enviou o CTe Complementar para a API; só resta avisar e recarregar a lista
      onModalComplementarSave() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.SUCCESS_EMIT_COMPLEMENTAR,
          modalComplementar: { visible: false, cte: null },
        }))
      },

      onModalSubstituicaoClose() {
        setState(prevState => ({
          ...prevState,
          modalSubstituicao: { visible: false, cte: null, dados: null },
        }))
      },

      // O modal já enviou o CTe de Substituição para a API; só resta avisar e recarregar a lista
      onModalSubstituicaoSave() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.SUCCESS_EMIT_SUBSTITUICAO,
          modalSubstituicao: { visible: false, cte: null, dados: null },
        }))
      },

      onModalMdfeClose() {
        setState(prevState => ({
          ...prevState,
          modalMdfe: { visible: false, ctes: null, data: null },
        }))
      },

      onModalMdfeSave(body) {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.EMIT_MDFE,
          modalMdfe: { ...prevState.modalMdfe, data: body },
        }))
      },

      emitMdfe(body) {
        postEmitMdfe(body)
          .then(() => {
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.SUCCESS_EMIT_MDFE,
            }))
          })
          .catch(err => {
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            }))
          })
      },
      onBtnEmitirMdfeClicked(ctes) {
        if (!ctes.length) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione ao menos um CTe para emitir MDFe',
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }
        if (ctes.some(c => c.status !== 'autorizado')) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione apenas CTe com status AUTORIZADO para emitir MDFe',
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }
        // CTe substituído deixou de valer: quem entra no MDFe é o CTe de Substituição
        const substituidos = ctes.filter(isCteSubstituido)
        if (substituidos.length) {
          setState(prevState => ({
            ...prevState,
            errorMessage: `CTe substituído não pode compor MDFe. Selecione o CTe de Substituição no lugar do(s) CTe nº ${substituidos
              .map(c => c.numero)
              .join(', ')}`,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }
        // CTe Complementar só complementa valores: não transporta carga e não entra no MDFe
        const complementares = ctes.filter(isCteComplementar)
        if (complementares.length) {
          setState(prevState => ({
            ...prevState,
            errorMessage: `CTe Complementar não pode compor MDFe. Desmarque o(s) CTe nº ${complementares
              .map(c => c.numero)
              .join(', ')}`,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }
        if (ctes.some(c => c.document_origin === 'outros')) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'CTe sem Nota Fiscal não pode compor MDFe. Desmarque-o(s) para continuar',
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }
        if (ctes.some(isCteSubcontratacao)) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'CTe de Subcontratação não pode compor MDFe. Desmarque-o(s) para continuar',
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }

        setState(prevState => ({
          ...prevState,
          modalMdfe: { visible: true, ctes, data: null },
        }))
      },
      handleStatusFiltroChange(selectedStatus) {
        aplicarFiltro('selectedStatus', selectedStatus || [])
      },

      handleCidadesFiltroChange(selectedCidades) {
        aplicarFiltro('selectedCidades', selectedCidades || [])
      },

      handleEmpresasFiltroChange(selectedEmpresas) {
        aplicarFiltro('selectedEmpresas', selectedEmpresas || [])
      },

      handleGlobalizadoFiltroChange(selectedGlobalizado) {
        aplicarFiltro('selectedGlobalizado', selectedGlobalizado)
      },

      handleTipoReferenciaFiltroChange(selectedTipoReferencia) {
        aplicarFiltro('selectedTipoReferencia', selectedTipoReferencia)
      },

      handleTipoDocumentoFiltroChange(selectedTipoDocumento) {
        aplicarFiltro('selectedTipoDocumento', selectedTipoDocumento)
      },

      handleTipoServicoFiltroChange(selectedTipoServico) {
        aplicarFiltro('selectedTipoServico', selectedTipoServico)
      },

      onSelecionarTodosChange(value) {
        setState(prev => {
          return {
            ...prev,
            filterdCtes: prev.filterdCtes.map(e => ({ ...e, selected: value })),
            selecionarTodos: value,
          }
        })
      },

      onClickGerarExcel(ctes) {
        if (!ctes.length) {
          ctes = state.ctes
        }
        const colunas = [
          [
            'Referência',
            'CTe Data Emissão',
            'CTe Número',
            'CTe Série',
            'CTe ICMS',
            'CTe Valor',
            'CTe Chave',
            'Tipo Documento',
            'Tipo Serviço',
            'Tipo Doc. Referência',
            'Doc. Referência',
            'Substituído por',
            'NF Data Emissão',
            'NF Número',
            'NF Série',
            'NF Valor',
            'NF Qte Cx',
            'Peso Bruto',
            'NF Chave',
            'Cidade Origem',
            'Cidade Destino',
            'Cliente',
            'Emitente',
            'Status',
            'Mensagem',
            'Empresa',
            'DACTE',
            'XML',
            'XML Cancelamento',
          ],
        ]
        ctes.forEach(c => {
          colunas.push([
            c.ref,
            new Date(c.data_emissao).toLocaleString(),
            c.numero, c.serie, c.icms_valor, c.valor_total, c.chave,
            c.tipo_documento_descricao || '',
            c.tipo_servico_descricao || '',
            c.documento_referencia_tipo || '',
            c.documento_referencia || '',
            isCteSubstituido(c) ? c.cte_substituto_numero : '',
            c.data_emissao_nf || '',
            c.identifier || '',
            c.invoice_serie || '',
            c.valor_nf != null ? c.valor_nf : '',
            c.qteSkuTotal != null ? c.qteSkuTotal : '',
            // Peso bruto (kg) declarado no CT-e: no Empresa-Cliente e no Globalizado, o total das notas
            c.peso_bruto != null ? c.peso_bruto : '',
            c.chave_nf || '',
            '', c.client_city, c.client_name,
            c.emissor_razao_social, statusExibicao(c), c.mensagem_sefaz,
            c.company_name,
            c.caminho_dacte,
            c.caminho_xml,
            c.caminho_xml_cancelamento,
          ])
        })

        gerarArquivoExcel('Relatorio CTe', colunas, `relatorio_cte.xlsx`)
      },

      onPesquisar(tp_id, pInicial, pFinal) {
        fetchCtes(tp_id, pInicial, pFinal)
      },

      onDownloadCte(ctes) {
        if (!ctes.length) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione ao menos um CTe para fazer o download',
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }

        window.open(urlDownloadCte(ctes.map(c => c.id)), '_blank')
      },
    }
  }, [])

  return { state, localDispatch }
}
